import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { SyllabusQuery, SyllabusSelectComponent } from '@examdojo/category';
import { IconComponent } from '@examdojo/core/icon';
import { MatTooltipTruncateDirective } from '@examdojo/core/mat-tooltip-truncate';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { ReferenceQuestionReviewType, ReviewPermissionsQuery } from '@examdojo/review';
import { AuthService, ProfileAvatarComponent, UserQuery } from '@examdojo/user';
import { TranslocoPipe } from '@jsverse/transloco';
import { map, of, switchMap } from 'rxjs';
import { ReferenceQuestionUrlParts, RootUrlParts, SyllabusUrlParts, ValidateUrlParts } from '../app.model';
import { HelpSidenavService } from '../services/help-sidenav.service';
import { getAllQuestionReviewConfigs } from '../validation/derivative-question-validation/derivative-question-validation.model';
import { getAllReferenceQuestionReviewConfigs } from '../validation/ref-question-validation/ref-question-validation.model';
import { NavigateOnActiveSyllabusChangeService } from './effects/navigate-on-active-syllabus-change.service';

@Component({
  selector: 'dojo-validation-shell',
  templateUrl: './validation-shell.component.html',
  styleUrl: './validation-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    MatSidenav,
    MatSidenavContent,
    MatSidenavContainer,
    IconComponent,
    MatMenu,
    MatMenuItem,
    RouterOutlet,
    SyllabusSelectComponent,
    RouterLinkActive,
    NgTemplateOutlet,
    TranslocoPipe,
    MatTooltipTruncateDirective,
    ProfileAvatarComponent,
    MatTooltip,
    MatMenuTrigger,
  ],
  providers: [NavigateOnActiveSyllabusChangeService],
})
export class ValidationShellComponent {
  constructor(
    private readonly authService: AuthService,
    private readonly reviewPermissionsQuery: ReviewPermissionsQuery,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly userQuery: UserQuery,
    private readonly helpSidenavService: HelpSidenavService,
  ) {
    this.effects.forEach((effect) => inject(effect));
  }

  readonly effects = [NavigateOnActiveSyllabusChangeService];

  readonly RootUrlParts = RootUrlParts;
  readonly SyllabusUrlParts = SyllabusUrlParts;
  readonly ValidateUrlParts = ValidateUrlParts;
  readonly ReferenceQuestionUrlParts = ReferenceQuestionUrlParts;
  readonly ReferenceQuestionReviewType = ReferenceQuestionReviewType;

  readonly activeSyllabusId$ = this.syllabusQuery.selectActiveId();
  readonly activeSyllabusSlug$ = this.syllabusQuery.selectActiveSlug();

  readonly state = connectState({
    syllabusSlug: this.activeSyllabusSlug$,
    allowedSyllabuses: this.reviewPermissionsQuery.selectAllowedSyllabuses(),
    permissions: this.activeSyllabusId$.pipe(
      switchMap((syllabusId) =>
        isNotNullish(syllabusId)
          ? this.reviewPermissionsQuery.selectSyllabusPermissions(syllabusId)
          : of(this.reviewPermissionsQuery.NO_PERMISSIONS),
      ),
    ),
    user: this.userQuery.active$,
    availableRefReviewConfigs: this.activeSyllabusId$.pipe(
      switchMap((syllabusId) =>
        isNotNullish(syllabusId)
          ? this.reviewPermissionsQuery
              .selectSyllabusPermissions(syllabusId)
              .pipe(
                map((permissions) =>
                  getAllReferenceQuestionReviewConfigs().filter(
                    (config) => permissions[config.permission as keyof typeof permissions],
                  ),
                ),
              )
          : of([]),
      ),
    ),
    availableDerReviewConfigs: this.activeSyllabusId$.pipe(
      switchMap((syllabusId) =>
        isNotNullish(syllabusId)
          ? this.reviewPermissionsQuery
              .selectSyllabusPermissions(syllabusId)
              .pipe(
                map((permissions) =>
                  getAllQuestionReviewConfigs().filter(
                    (config) => permissions[config.permission as keyof typeof permissions],
                  ),
                ),
              )
          : of([]),
      ),
    ),
  });

  toggleHelpSidenav() {
    this.helpSidenavService.toggle();
  }

  async signOut() {
    await this.authService.signOut();
  }
}
